/**
 * Veranstaltungs-Detail (Design-Update v2, Anhang E "Veranstaltungs-Detail Redesign"):
 * Status-Stepper + "Nächster Schritt"-Banner, Angebot, Abrechnung (Kostenübernahme/
 * Rechnungen/Zahlungen gebündelt), Aktivität (E-Mail-Verlauf + Verlauf gebündelt) links;
 * Eckdaten, Kunde, Aufgaben rechts. Alles gegen die Live-API.
 */
import { useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { openPrintableDocument } from "../lib/print";
import { openAuthenticatedFile } from "../lib/files";
import type { Customer, EventRow, EventStatus, Location, OfferOption, Payment, Task } from "../ui/types";
import {
  EVENT_STATUS,
  PAYMENT_STATUS,
  PAYMENT_TYPE_LABEL,
  PILL_CLASSES,
  daysUntil,
  formatDate,
  formatDateTime,
  formatWeekdayDate,
  money,
  parseGermanEurosToCents,
} from "../ui/format";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, StatusPill } from "../ui/components";

/** Wetter-Widget (Datenanreicherung Welle A) — Antwortform von GET /api/events/:id/weather. */
interface DayForecast {
  date: string;
  description: string | null;
  icon: string | null;
  tempMinC: number | null;
  tempMaxC: number | null;
  precipitationMm: number | null;
}

/** Backlog #41: Chat-Anhang (events.details.attachments), gespiegelt aus lib/event-attachments.ts. */
interface EventAttachment {
  id: string;
  contentType: string;
  uploadedAt: string;
}

/** Client-seitiger Spiegel von apps/api/src/lib/weather.ts#isWithinForecastWindow (~ -1 bis +10
 *  Tage) -- rein, damit ausserhalb des Fensters gar kein Request an die Wetter-Route abgeht. */
function isWithinForecastWindowClient(dateStr: string): boolean {
  const target = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return false;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((target.getTime() - today) / 86_400_000);
  return diffDays >= -1 && diffDays <= 10;
}

/** M-09: Persistierte Storno-Todos (events.details.cancellationTodos) — überleben Reload und Nutzerwechsel. */
interface CancellationTodos {
  cancelledAt?: string;
  canceledPayments?: number;
  openInvoices?: { id: string; invoiceNumber: string | null; invoiceType: string }[];
  paidPayments?: { id: string; amountCents: number }[];
}
interface CostAcceptance {
  id: string;
  status: "draft" | "sent" | "signed" | "declined" | "withdrawn";
  signUrl: string | null;
  signedPdfUrl: string | null;
  signerName: string | null;
  amountGrossCents: number | null;
  signedAt: string | null;
  createdAt: string;
}
const CA_STATUS: Record<string, { label: string; cls: string }> = {
  sent: { label: "Gesendet – wartet auf Unterschrift", cls: PILL_CLASSES.warn },
  signed: { label: "Signiert – verbindlich", cls: PILL_CLASSES.success },
  declined: { label: "Abgelehnt", cls: PILL_CLASSES.danger },
  withdrawn: { label: "Zurückgezogen", cls: PILL_CLASSES.neutral },
  draft: { label: "Entwurf", cls: PILL_CLASSES.neutral },
};
const caMeta = (s: string): { label: string; cls: string } => CA_STATUS[s] ?? { label: s, cls: PILL_CLASSES.neutral };
const NOT_REQUESTED = { label: "Nicht angefordert", cls: PILL_CLASSES.neutral };

interface Invoice {
  id: string;
  invoiceType: "invoice" | "deposit" | "final" | "credit";
  status: "draft" | "open" | "paid" | "voiding" | "voided" | "failed";
  invoiceNumber: string | null;
  amountGrossCents: number | null;
  relatedInvoiceId: string | null;
  sentAt: string | null;
  createdAt: string;
  pdfUrl: string | null;
}
const INV_TYPE_LABEL: Record<Invoice["invoiceType"], string> = {
  invoice: "Rechnung",
  deposit: "Anzahlung",
  final: "Schlussrechnung",
  credit: "Gutschrift",
};
const INV_STATUS: Record<string, { label: string; cls: string }> = {
  open: { label: "Erstellt – offen", cls: PILL_CLASSES.warn },
  paid: { label: "Bezahlt", cls: PILL_CLASSES.success },
  voiding: { label: "Wird storniert …", cls: PILL_CLASSES.warn },
  voided: { label: "Storniert", cls: PILL_CLASSES.danger },
  failed: { label: "Fehlgeschlagen", cls: PILL_CLASSES.danger },
  draft: { label: "Entwurf", cls: PILL_CLASSES.neutral },
};
const invMeta = (s: string): { label: string; cls: string } => INV_STATUS[s] ?? { label: s, cls: PILL_CLASSES.neutral };

/** Versand-Snapshot (offer_history) — items/days nur ab Block 3 vorhanden (aeltere Versionen: Summary-only). */
interface OfferHistoryOptionSnapshot {
  label: string;
  title: string | null;
  guestCount: number;
  amountTotalCents: number;
  items?: unknown[];
  days?: unknown[];
}
interface OfferHistoryEntry {
  id: string;
  version: number;
  sentAt: string | null;
  optionsSnapshot: OfferHistoryOptionSnapshot[];
}

interface ActivityEntry {
  id: string;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}
/** Anzeigetext + Icon je Aktionscode (lib/activity-log.ts). Unbekannte Codes -> Rohcode + Standard-Icon. */
const ACTIVITY_LABEL: Record<string, { label: string; icon: string }> = {
  "event.status_changed": { label: "Status geändert", icon: "sync_alt" },
  "offer.sent": { label: "Angebot gesendet", icon: "send" },
  "offer.chosen": { label: "Angebot angenommen", icon: "celebration" },
  "cost_acceptance.requested": { label: "Kostenübernahme angefordert", icon: "draw" },
  "cost_acceptance.signed": { label: "Kostenübernahme signiert", icon: "verified" },
  "invoice.created": { label: "Rechnung erstellt", icon: "receipt_long" },
  "invoice.sent": { label: "Rechnung gesendet", icon: "mail" },
  "invoice.cancelled": { label: "Rechnung storniert (Gutschrift)", icon: "undo" },
  "inquiry.created": { label: "Anfrage eingegangen", icon: "inbox" },
  "offer.restored": { label: "Version als Entwurf wiederhergestellt", icon: "history" },
};
const ACTOR_LABEL: Record<ActivityEntry["actorType"], string> = { user: "Team", customer: "Kunde", system: "System" };

/** Spiegel der Server-Whitelist (lib/event-status.ts) fuer die Aktions-Buttons. */
const ALLOWED: Record<EventStatus, EventStatus[]> = {
  inquiry: ["offer_draft", "offer_sent", "no_response", "cancelled"],
  offer_draft: ["offer_sent", "cancelled"],
  offer_sent: ["offer_chosen", "offer_declined", "no_response", "cancelled"],
  offer_chosen: ["paid", "cancelled"],
  offer_declined: ["offer_draft", "cancelled"],
  paid: ["completed", "cancelled", "payment_failed"],
  payment_failed: ["paid", "cancelled"],
  no_response: ["offer_draft", "cancelled"],
  completed: [],
  cancelled: [],
};

/** "Nächster Schritt"-Text je Status (Anhang E, Card L1) — reine Empfehlung, keine Automatisierung. */
const NEXT_STEP_COPY: Partial<Record<EventStatus, string>> = {
  inquiry: "Anfrage prüfen und ein Angebot erstellen",
  offer_draft: "Angebot fertigstellen und an den Kunden senden",
  offer_sent: "Rückmeldung des Kunden einholen",
  offer_declined: "Neues Angebot erstellen oder Vorgang schließen",
  offer_chosen: "Rechnung/Zahlung einrichten",
  payment_failed: "Zahlung klären",
  paid: "Durchführung vorbereiten",
  no_response: "Nachfassen oder Status klären",
};

const STEPPER: EventStatus[] = ["inquiry", "offer_draft", "offer_sent", "offer_chosen", "paid", "completed"];

const CARD_TITLE =
  "font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface";
const SIDE_TITLE =
  "font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface";

/** Versandstatus einer geloggten Mail (Migration 78). */
const EMAIL_STATUS_LABEL: Record<string, string> = {
  sent: "Gesendet",
  failed: "Fehlgeschlagen",
  delivered: "Zugestellt",
  bounced: "Unzustellbar",
  complained: "Beschwerde",
};

// KI-Klassifikation eingehender Mails (Welle 3/5b) - rein informativ, keine Automatisierung dahinter.
const MAIL_CLASSIFICATION_LABEL: Record<string, string> = {
  accepted: "Zusage",
  declined: "Absage",
  question: "Frage",
  other: "Sonstiges",
};

interface EmailRow {
  id: string;
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  bodyText: string | null;
  status: string | null;
  error: string | null;
  aiClassification: string | null;
  receivedAt: string;
}

/** Aktivität-Karte (Anhang E, Card L4): E-Mail-Verlauf + Status-Verlauf in einem Feed. */
type FeedEntry =
  | { kind: "email"; id: string; at: string; email: EmailRow }
  | { kind: "status"; id: string; at: string; activity: ActivityEntry };

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const eventQ = useApi<EventRow>(id ? `/api/events/${id}` : null);
  const offersQ = useApi<OfferOption[]>(id ? `/api/events/${id}/offers` : null);
  const paymentsQ = useApi<Payment[]>(id ? `/api/events/${id}/payments` : null);
  const event = eventQ.data;
  const customerQ = useApi<Customer>(event ? `/api/customers/${event.customerId}` : null);
  const caQ = useApi<CostAcceptance | null>(id ? `/api/events/${id}/cost-acceptance` : null);
  const invoicesQ = useApi<Invoice[]>(id ? `/api/events/${id}/invoices` : null);
  const emailsQ = useApi<EmailRow[]>(id ? `/api/events/${id}/emails` : null);
  const activityQ = useApi<ActivityEntry[]>(id ? `/api/events/${id}/activity` : null);
  const tasksQ = useApi<Task[]>(id ? `/api/tasks?eventId=${id}` : null);
  const historyQ = useApi<OfferHistoryEntry[]>(id ? `/api/events/${id}/offer-history` : null);
  // Datenanreicherung Welle A: fehlende tenant_settings-Zeile -> {data: null} -> "alle Flags false"
  // (nie Fehler, nie Default-an).
  const settingsQ = useApi<{ wetterEnabled?: boolean } | null>("/api/settings/payment-terms");
  const wetterEnabled = settingsQ.data?.wetterEnabled ?? false;
  // Koordinaten-Aufloesung laeuft serverseitig (GET .../weather) -- hier nur das ~10-Tage-Fenster
  // pruefen, damit ausserhalb davon gar kein Request abgeht.
  const weatherWindowOk = eventQ.data?.eventDate ? isWithinForecastWindowClient(eventQ.data.eventDate) : false;
  const shouldFetchWeather = Boolean(id && wetterEnabled && eventQ.data?.locationType && weatherWindowOk);
  const weatherQ = useApi<DayForecast | null>(shouldFetchWeather ? `/api/events/${id}/weather` : null);

  // M-09: Storno-Todos kommen persistiert aus events.details.cancellationTodos (der Server schreibt
  // sie atomar mit dem Storno) — sie überleben Reload/Tabwechsel und sind für alle Nutzer sichtbar.
  const cancellationTodos =
    event?.status === "cancelled"
      ? ((event.details as { cancellationTodos?: CancellationTodos } | null)?.cancellationTodos ?? null)
      : null;

  const [busy, setBusy] = useState(false);
  const [historyBusy, setHistoryBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [caBusy, setCaBusy] = useState(false);
  const [caTest, setCaTest] = useState(false);
  const [invBusy, setInvBusy] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState<"all" | "emails" | "status">("all");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [taskBusy, setTaskBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const locationsQ = useApi<Location[]>(editOpen ? "/api/locations" : null);

  async function toggleTask(t: Task) {
    setTaskBusy(true);
    try {
      await apiMutate(`/api/tasks/${t.id}`, "PATCH", { status: t.status === "done" ? "open" : "done" });
      tasksQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTaskBusy(false);
    }
  }

  async function addTask(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id || !newTaskTitle.trim()) return;
    setTaskBusy(true);
    try {
      await apiMutate("/api/tasks", "POST", { title: newTaskTitle.trim(), eventId: id });
      setNewTaskTitle("");
      tasksQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTaskBusy(false);
    }
  }

  async function saveEdit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    const form = new FormData(e.currentTarget);
    setEditBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}`, "PATCH", {
        // A-08: leer gelassene Felder als null senden (Server-Schema erlaubt nullable) statt sie
        // als undefined wegzulassen - sonst kann der Nutzer einen Wert nicht wieder loeschen.
        occasion: (form.get("occasion") as string) || null,
        serviceType: (form.get("serviceType") as string) || null,
        eventDate: (form.get("eventDate") as string) || undefined,
        timeFrom: (form.get("timeFrom") as string) || null,
        timeTo: (form.get("timeTo") as string) || null,
        guestCount: form.get("guestCount") ? Number(form.get("guestCount")) : undefined,
        location: (form.get("location") as string) || null,
        // A-01: locationId nur mitsenden, wenn die Options-Liste geladen ist. Sonst wuerde das
        // Select (defaultValue greift bei async nachgeladenen Optionen nicht) leer wirken und den
        // zugewiesenen Ort beim Speichern still auf null setzen.
        ...(locationsQ.data ? { locationId: (form.get("locationId") as string) || null } : {}),
        locationType: (form.get("locationType") as string) || null,
        offerLanguage: (form.get("offerLanguage") as string) || undefined,
      });
      setEditOpen(false);
      eventQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditBusy(false);
    }
  }

  async function printKitchenSheet() {
    if (!id) return;
    setPrintBusy(true);
    setActionError(null);
    try {
      await openPrintableDocument(`/api/events/${id}/kitchen-sheet`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintBusy(false);
    }
  }

  /** Angebots-Dokument (Block 2): druckfertige HTML-Ansicht einer Angebotsvariante. */
  async function printOfferDocument(offerId: string) {
    setPrintBusy(true);
    setActionError(null);
    try {
      await openPrintableDocument(`/api/offers/${offerId}/document`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintBusy(false);
    }
  }

  async function requestCostAcceptance() {
    if (!id) return;
    setCaBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/cost-acceptance`, "POST", { test: caTest });
      caQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /no_template|Vorlage/i.test(msg)
          ? "eSign-Vorlage fehlt: unter Einstellungen → Zahlungen die Template-ID hinterlegen."
          : /esign_not_configured|503/i.test(msg)
            ? "eSign ist nicht konfiguriert (COMPOSIO_API_KEY im Worker fehlt)."
            : msg,
      );
    } finally {
      setCaBusy(false);
    }
  }

  async function createInvoice(type: "invoice" | "deposit" | "final") {
    if (!id) return;
    setInvBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/invoice`, "POST", { type });
      invoicesQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /invoice_not_configured|503/i.test(msg)
          ? "LexOffice ist nicht konfiguriert (LEXOFFICE_API_KEY im Worker fehlt)."
          : /no_active_offer_options/i.test(msg)
            ? "Es gibt keine aktive Angebotsvariante zum Abrechnen."
            : /no_deposit_terms/i.test(msg)
              ? "Keine Anzahlung in den Zahlungsbedingungen hinterlegt (Einstellungen → Zahlungen)."
              : /no_deposit_invoice/i.test(msg)
                ? "Für die Schlussrechnung zuerst die Anzahlungsrechnung erstellen."
                : /nothing_to_bill/i.test(msg)
                  ? "Nach Anzahlungsabzug ist kein Restbetrag offen."
                  : msg,
      );
    } finally {
      setInvBusy(false);
    }
  }

  async function sendInvoice(invoiceId: string) {
    if (!id) return;
    setInvBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/invoices/${invoiceId}/send`, "POST", {});
      invoicesQ.reload();
    } catch (err) {
      // H-40: Versandfehler ehrlich anzeigen - sentAt bleibt serverseitig leer, der Button
      // bleibt "An Kunden senden" (Retry). Vorher meldete die Route 200 trotz Mail-Fehler.
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /mail_not_configured/i.test(msg)
          ? "E-Mail-Versand ist nicht konfiguriert – die Rechnung wurde NICHT zugestellt."
          : /no_recipient/i.test(msg)
            ? "Der Kunde hat keine E-Mail-Adresse – die Rechnung wurde NICHT zugestellt."
            : /send_failed/i.test(msg)
              ? "E-Mail-Versand fehlgeschlagen – die Rechnung wurde NICHT zugestellt. Bitte erneut senden."
              : msg,
      );
      invoicesQ.reload();
    } finally {
      setInvBusy(false);
    }
  }

  async function cancelInvoice(invoiceId: string) {
    if (!id) return;
    // N-12: buchhalterisch wirksam (Gutschrift) — vor Einfachklick absichern, analog cancelEvent.
    if (!window.confirm("Rechnung wirklich stornieren? Es wird eine Gutschrift erzeugt.")) return;
    setInvBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/invoices/${invoiceId}/cancel`, "POST", {});
      invoicesQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setInvBusy(false);
    }
  }

  /** Versionsarchiv: eine gesendete Version als neuen Entwurf kopieren (nur mit Positions-Snapshot). */
  async function restoreVersion(version: number) {
    if (!id) return;
    if (!window.confirm(`Version v${version} als neuen Entwurf kopieren? Es entsteht eine zusätzliche Angebotsvariante.`)) return;
    setHistoryBusy(version);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/offer-history/${version}/restore`, "POST", {});
      offersQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /snapshot_incomplete/i.test(msg)
          ? "Diese Version wurde vor der Positions-Archivierung versendet und enthält keine Positionen – nur als Zusammenfassung verfügbar."
          : msg,
      );
    } finally {
      setHistoryBusy(null);
    }
  }

  async function transition(to: EventStatus) {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/transition`, "POST", { to });
      eventQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** Storno-Workflow: Vorgang abbrechen (Owner/Admin) — entwertet offene Zahlungen, zeigt Todos. */
  async function cancelEvent() {
    if (!id) return;
    if (!window.confirm("Vorgang wirklich stornieren? Offene Zahlungen werden entwertet.")) return;
    setBusy(true);
    setActionError(null);
    try {
      // M-09: Die Todos werden serverseitig in events.details persistiert — eventQ.reload()
      // holt sie, ein lokaler React-State ist nicht mehr noetig (ging bei Reload verloren).
      await apiMutate(`/api/events/${id}/cancel`, "POST", {});
      eventQ.reload();
      paymentsQ.reload();
      invoicesQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /forbidden_role/i.test(msg)
          ? "Nur Owner/Admin dürfen stornieren."
          : /already_cancelled|409/i.test(msg)
            ? "Vorgang ist bereits storniert."
            : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  /** "Erinnerung senden" (Card L1, Anhang E): stellt die AKTUELLE Angebotsversion erneut zu.
   *  H-25: eigene Resend-Route — erzeugt KEINE neue Version und KEINEN neuen Snapshot mehr
   *  (vorher lief das ueber /offers/send und jede Erinnerung wurde eine neue Rechtsversion). */
  async function sendReminder() {
    if (!id) return;
    setReminderBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/offers/resend`, "POST", {});
      offersQ.reload();
      eventQ.reload();
      activityQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setReminderBusy(false);
    }
  }

  async function createPayment(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    const form = new FormData(e.currentTarget);
    // H7: „1.500" darf nicht still zu 1,50 € werden — ungültige Eingabe als Fehler melden.
    const amountCents = parseGermanEurosToCents((form.get("amount") as string) ?? "");
    if (amountCents == null) {
      setActionError("Ungültiger Betrag – bitte als Zahl eingeben, z. B. 1.500,00.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/payments`, "POST", {
        amountCents,
        paymentType: form.get("paymentType"),
        dueDate: (form.get("dueDate") as string) || undefined,
      });
      setPayOpen(false);
      paymentsQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** Standard-Zahlungsplan (Anzahlung + Restzahlung) aus der Angebotssumme erzeugen. */
  async function createPaymentPlan() {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/payment-plan`, "POST", {});
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /no_amount/i.test(msg)
          ? "Kein Angebotsbetrag – zuerst eine Angebotsvariante wählen."
          : /no_event_date/i.test(msg)
            ? "Kein Event-Datum – ohne Datum bekäme die Restzahlung keine Fälligkeit. Erst Datum setzen, dann Zahlungsplan anlegen."
            : /plan_exists|409/i.test(msg)
              ? "Es gibt bereits Zahlungen für diesen Vorgang."
              : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  /** Aktion auf eine einzelne Zahlung (Link erstellen/senden, bezahlt markieren, löschen). */
  async function paymentAction(payment: Payment, action: "checkout-link" | "send-link" | "paid" | "delete") {
    if (!id) return;
    // N-12: Löschen ist buchhalterisch wirksam — vor Einfachklick absichern.
    if (action === "delete" && !window.confirm("Zahlung wirklich löschen?")) return;
    // M-13: "Bezahlt" ist terminal und stößt im Hintergrund eine ECHTE LexOffice-Rechnung an –
    // ein Fehlklick hat reale Belegfolgen. Bestätigung mit Betrag, Kunde und Konsequenz.
    if (
      action === "paid" &&
      !window.confirm(
        `${PAYMENT_TYPE_LABEL[payment.paymentType] ?? "Zahlung"} über ${money(payment.amountCents)}${customerQ.data ? ` (${customerQ.data.name})` : ""} als bezahlt markieren?\n\nDer Status wird endgültig auf „bezahlt" gesetzt und es wird automatisch eine echte Rechnung erzeugt und an den Kunden gesendet.`,
      )
    )
      return;
    setBusy(true);
    setActionError(null);
    try {
      const paymentId = payment.id;
      if (action === "paid") await apiMutate(`/api/payments/${paymentId}`, "PATCH", { status: "succeeded" });
      else if (action === "delete") await apiMutate(`/api/payments/${paymentId}`, "DELETE");
      else await apiMutate(`/api/payments/${paymentId}/${action}`, "POST", {});
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /payment_link_not_configured|mail_not_configured|503/i.test(msg)
          ? "Stripe/Mailversand ist im Worker nicht konfiguriert (COMPOSIO_API_KEY)."
          : /no_link/i.test(msg)
            ? "Erst einen Zahlungslink erstellen."
            : /no_customer_email/i.test(msg)
              ? "Kein Kunde mit E-Mail am Vorgang."
              : /send_failed/i.test(msg)
                ? "Zahlungslink-Mail konnte NICHT versendet werden – bitte erneut versuchen."
                : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  const feed = useMemo<FeedEntry[]>(() => {
    const emails: FeedEntry[] = (emailsQ.data ?? []).map((m) => ({ kind: "email", id: `email-${m.id}`, at: m.receivedAt, email: m }));
    const statuses: FeedEntry[] = (activityQ.data ?? []).map((a) => ({ kind: "status", id: `status-${a.id}`, at: a.createdAt, activity: a }));
    return [...emails, ...statuses].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [emailsQ.data, activityQ.data]);
  const filteredFeed = useMemo(
    () => (activityFilter === "all" ? feed : feed.filter((e) => e.kind === (activityFilter === "emails" ? "email" : "status"))),
    [feed, activityFilter],
  );
  /** G6: mehrere Zustellungen derselben ausgehenden Mail (z. B. mehrere Empfaenger/Retries)
   *  zu einer Zeile buendeln statt jede einzeln aufzulisten. */
  const groupedFeed = useMemo(() => {
    const groups: { key: string; primary: FeedEntry; extra: FeedEntry[] }[] = [];
    for (const entry of filteredFeed) {
      const last = groups[groups.length - 1];
      if (
        entry.kind === "email" &&
        entry.email.direction === "outbound" &&
        entry.email.subject &&
        last?.primary.kind === "email" &&
        last.primary.email.direction === "outbound" &&
        last.primary.email.subject === entry.email.subject
      ) {
        last.extra.push(entry);
      } else {
        groups.push({ key: entry.id, primary: entry, extra: [] });
      }
    }
    return groups;
  }, [filteredFeed]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  function toggleGroup(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  /** Ein Feed-Eintrag vom Typ "email" als <li> (G7: Recovery-CTAs bei fehlgeschlagener Zustellung). */
  function EmailEntryItem({ entry }: { entry: Extract<FeedEntry, { kind: "email" }> }) {
    const failed = entry.email.status === "failed" || entry.email.status === "bounced" || entry.email.status === "complained";
    return (
      <li className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            <Icon name={entry.email.direction === "inbound" ? "call_received" : "call_made"} className="text-[16px] text-primary" />
            {entry.email.direction === "outbound" ? `an ${entry.email.toAddr ?? "?"}` : entry.email.fromAddr}
          </span>
          <span className="flex items-center gap-2">
            {entry.email.aiClassification && (
              <span className="rounded-full bg-secondary/10 px-2 py-0.5 font-label-md text-xs font-medium text-secondary dark:text-secondary-fixed-dim">
                {MAIL_CLASSIFICATION_LABEL[entry.email.aiClassification] ?? entry.email.aiClassification}
              </span>
            )}
            {entry.email.status && (
              <span
                className={`rounded-full px-2 py-0.5 font-label-md text-xs font-medium ${failed ? "bg-error/10 text-error" : "bg-primary/10 text-primary"}`}
              >
                {EMAIL_STATUS_LABEL[entry.email.status] ?? entry.email.status}
              </span>
            )}
            <span className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{formatDate(entry.email.receivedAt)}</span>
          </span>
        </div>
        {entry.email.subject && <p className="mt-1 font-body-sm text-body-sm font-medium text-on-surface dark:text-inverse-on-surface">{entry.email.subject}</p>}
        {entry.email.error && <p className="mt-1 whitespace-pre-wrap break-all font-body-sm text-body-sm text-error">{entry.email.error}</p>}
        {entry.email.bodyText && <p className="mt-1 line-clamp-3 whitespace-pre-wrap font-body-sm text-body-sm text-on-surface-variant">{entry.email.bodyText}</p>}
        {failed && (
          <div className="mt-2.5 flex items-center gap-2 border-t border-outline-variant/30 pt-2.5 dark:border-outline/30">
            <Link to="/einstellungen" className="btn-secondary !py-1.5 !text-[11.5px]">
              <Icon name="link" className="text-[14px]" /> Konto verbinden
            </Link>
            {id && (
              <Link to={`/veranstaltungen/${id}/angebot`} className="btn-secondary !py-1.5 !text-[11.5px]">
                <Icon name="refresh" className="text-[14px]" /> Erneut senden
              </Link>
            )}
          </div>
        )}
      </li>
    );
  }

  if (eventQ.loading) return <LoadingRows rows={6} />;
  if (eventQ.error) return <ErrorNote message={eventQ.error} />;
  if (!event) return <EmptyState icon="event_busy" title="Event nicht gefunden" />;

  const stepIdx = STEPPER.indexOf(event.status);
  const customer = customerQ.data;
  const offers = (offersQ.data ?? []).filter((o) => o.isActive);
  const payments = paymentsQ.data ?? [];
  const invoices = invoicesQ.data ?? [];
  const paidCents = payments
    .filter((p) => p.status === "succeeded")
    .reduce((s, p) => s + p.amountCents, 0);
  // M1 (Bughunt 2026-07-11): "Offen" = Geld noch nicht eingegangen = pending + processing
  // ("In Bearbeitung"). Gleiche Definition wie die Zahlungen-Übersicht (Offen-KPI). Der Chip zeigte
  // vorher faelschlich `paidCents` unter dem Label "Offen" ("Offen · 0 €" trotz 900 € in Bearbeitung).
  const openCents = payments
    .filter((p) => p.status === "pending" || p.status === "processing")
    .reduce((s, p) => s + p.amountCents, 0);
  const timeRange = event.timeFrom
    ? `${event.timeFrom.slice(0, 5)} – ${event.timeTo?.slice(0, 5) ?? "?"} Uhr`
    : null;
  const isPast = event.eventDate ? new Date(event.eventDate) < new Date(new Date().toDateString()) : false;
  const offerSentCount = (activityQ.data ?? []).filter((a) => a.action === "offer.sent").length;
  const hasDeposit = invoices.some((i) => i.invoiceType === "deposit" && i.status !== "voided");
  const hasFinalOrFull = invoices.some((i) => (i.invoiceType === "final" || i.invoiceType === "invoice") && i.status !== "voided");
  const showNextStep = event.status !== "completed" && event.status !== "cancelled";

  return (
    <>
      {/* Kopf: Breadcrumb + Status-Pill, Titel, Eckdaten-Zeile, Aktion */}
      <section className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-outline-variant/30 pb-6 dark:border-outline/30">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/veranstaltungen"
              className="font-label-md text-label-md uppercase tracking-wider text-secondary hover:text-primary dark:text-secondary-fixed-dim"
            >
              Veranstaltungen
            </Link>
            <span className="h-1 w-1 rounded-full bg-outline-variant" />
            <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              {event.number ?? event.id.slice(0, 8)}
            </span>
            <span className="h-1 w-1 rounded-full bg-outline-variant" />
            <StatusPill meta={EVENT_STATUS[event.status]} />
          </div>

          <h1 className="font-display-lg text-3xl font-bold tracking-tight text-on-background dark:text-inverse-on-surface sm:text-headline-md md:text-display-lg">
            {event.occasion ?? event.number ?? "Veranstaltung"}
          </h1>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
            <span className="flex items-center gap-2">
              <Icon name="calendar_today" className="text-[18px]" />
              {formatWeekdayDate(event.eventDate)}
              {daysUntil(event.eventDate) ? ` · ${daysUntil(event.eventDate)}` : ""}
              {timeRange ? ` · ${timeRange}` : ""}
            </span>
            {isPast && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAEFD9] px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-[#96650D]">
                <Icon name="warning" className="text-[13px]" /> Termin liegt in der Vergangenheit
              </span>
            )}
            {event.location ? (
              <span className="flex items-center gap-2">
                <Icon name="location_on" className="text-[18px]" />
                {event.location}
              </span>
            ) : (
              <button type="button" className="flex items-center gap-2 text-primary hover:underline" onClick={() => setEditOpen(true)}>
                <Icon name="location_on" className="text-[18px]" /> Ort · ergänzen
              </button>
            )}
            {event.guestCount != null && (
              <span className="flex items-center gap-2">
                <Icon name="group" className="text-[18px]" />
                {event.guestCount} Gäste
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" className="btn-secondary !py-2" onClick={() => setEditOpen(true)}>
            <Icon name="edit" className="text-[18px]" /> Bearbeiten
          </button>
          <button
            type="button"
            className="btn-secondary !py-2"
            disabled={printBusy}
            onClick={() => void printKitchenSheet()}
            title="Küchenzettel drucken (Positionen der gewählten Variante, ohne Preise)"
          >
            <Icon name="print" className="text-[18px]" /> Küchenzettel
          </button>
          {ALLOWED[event.status].includes("completed") && (
            <button type="button" className="btn-primary !py-2" disabled={busy} onClick={() => void transition("completed")}>
              <Icon name="task_alt" className="text-[18px]" /> Als durchgeführt markieren
            </button>
          )}
          <Link to={`/veranstaltungen/${event.id}/angebot`} className="btn-primary">
            <Icon name="request_quote" className="text-[18px]" /> Angebots-Builder
          </Link>
        </div>
      </section>

      {actionError && <ErrorNote message={actionError} />}

      {/* Bento-Grid: links Stepper/Angebot/Abrechnung/Aktivität (1fr), rechts Eckdaten/Kunde/Aufgaben (320px) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        {/* Hauptspalte */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* L1: Status-Stepper + Nächster Schritt */}
          {stepIdx >= 0 && (
            <section className="card p-0">
              <div className="overflow-x-auto p-6 pb-4">
                <div className="flex min-w-[560px] items-center">
                  {STEPPER.map((s, i) => (
                    <div key={s} className="flex flex-1 items-center last:flex-none">
                      <div className="flex flex-col items-center gap-1.5">
                        <span
                          className={`flex h-[22px] w-[22px] items-center justify-center rounded-full font-label-md text-[10.5px] font-bold ${
                            i < stepIdx
                              ? "bg-primary text-on-primary"
                              : i === stepIdx
                                ? "border-2 border-primary bg-primary-container/50 text-primary"
                                : "border border-outline-variant text-on-surface-variant dark:border-outline dark:text-secondary-fixed-dim"
                          }`}
                        >
                          {i < stepIdx ? <Icon name="check" className="text-[13px]" /> : i + 1}
                        </span>
                        <span
                          className={`whitespace-nowrap font-label-md text-[10.5px] ${
                            i === stepIdx ? "font-bold text-primary" : "font-medium text-secondary dark:text-secondary-fixed-dim"
                          }`}
                        >
                          {EVENT_STATUS[s].label}
                        </span>
                      </div>
                      {i < STEPPER.length - 1 && (
                        <div
                          className={`mx-2 h-0.5 flex-1 ${
                            i < stepIdx ? "bg-primary" : "bg-outline-variant/40 dark:bg-outline/40"
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {showNextStep && (
                <div className="mx-6 mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-low p-3.5 dark:border-outline/30 dark:bg-surface-variant/10">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-container text-primary">
                    <Icon name="bolt" className="text-[18px]" />
                  </div>
                  <div className="min-w-[220px] flex-1">
                    <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                      Nächster Schritt: {NEXT_STEP_COPY[event.status] ?? "Status prüfen"}
                    </p>
                    {event.status === "offer_sent" && (
                      <p className="mt-0.5 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                        Angebot {offerSentCount > 0 ? `${offerSentCount}× ` : ""}gesendet
                        {event.offerSentAt ? `, zuletzt ${formatDateTime(event.offerSentAt)}` : ""} — noch keine Auswahl getroffen.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {event.status === "offer_sent" && (
                      <button type="button" className="btn-primary !py-2" disabled={reminderBusy} onClick={() => void sendReminder()}>
                        <Icon name="send" className="text-[16px]" /> {reminderBusy ? "Wird gesendet …" : "Erinnerung senden"}
                      </button>
                    )}
                    {/* CX-Audit 2026-07-17: Der Banner NANNTE den nächsten Schritt, bot ihn aber
                        nur bei offer_sent auch an. Jetzt führt jeder empfohlene Schritt direkt
                        zum Ort der Handlung (Builder bzw. Abrechnungskarte). */}
                    {["inquiry", "offer_draft", "offer_declined", "no_response"].includes(event.status) && (
                      <Link to={`/veranstaltungen/${event.id}/angebot`} className="btn-primary !py-2">
                        <Icon name="edit_document" className="text-[16px]" /> Angebot erstellen
                      </Link>
                    )}
                    {["offer_chosen", "payment_failed"].includes(event.status) && (
                      <button
                        type="button"
                        className="btn-primary !py-2"
                        onClick={() => document.getElementById("abrechnung")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      >
                        <Icon name="receipt_long" className="text-[16px]" /> Zur Abrechnung
                      </button>
                    )}
                    <div className="relative">
                      <button type="button" className="btn-secondary !py-2" onClick={() => setStatusMenuOpen((v) => !v)}>
                        Status ändern <Icon name="expand_more" className="text-[16px]" />
                      </button>
                      {statusMenuOpen && (
                        <div className="absolute right-0 z-10 mt-1 w-60 rounded-lg border border-outline-variant/40 bg-surface-container-lowest py-1 shadow-lg dark:border-outline/30 dark:bg-surface-dim">
                          {ALLOWED[event.status].length === 0 ? (
                            <p className="px-3 py-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                              Endstatus – keine Übergänge mehr.
                            </p>
                          ) : (
                            ALLOWED[event.status].map((to) => (
                              <button
                                key={to}
                                type="button"
                                disabled={busy}
                                onClick={() => {
                                  setStatusMenuOpen(false);
                                  // H8: „Storniert" muss über den vollen Storno-Workflow laufen
                                  // (Payment-Void + Rollen-Gate), nicht bloß den Status setzen.
                                  if (to === "cancelled") void cancelEvent();
                                  else void transition(to);
                                }}
                                className={`block w-full px-3 py-2 text-left font-body-sm text-body-sm hover:bg-surface-container-high dark:hover:bg-surface-variant/20 ${
                                  to === "cancelled" ? "text-error" : "text-on-background dark:text-inverse-on-surface"
                                }`}
                              >
                                {EVENT_STATUS[to].label}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* L2: Angebot */}
          <section className="card p-6">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className={CARD_TITLE}>Angebot</h3>
                {event.currentOfferVersion > 0 && (
                  <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:bg-surface-variant/30 dark:text-secondary-fixed-dim">
                    v{event.currentOfferVersion}
                  </span>
                )}
              </div>
              <Link
                to={`/veranstaltungen/${event.id}/angebot`}
                className="font-label-md text-label-md text-primary hover:underline"
              >
                Im Builder bearbeiten
              </Link>
            </div>
            {offersQ.loading ? (
              <LoadingRows rows={2} />
            ) : offers.length === 0 ? (
              <EmptyState
                icon="request_quote"
                title="Noch kein Angebot"
                hint="Im Angebots-Builder Optionen anlegen und an den Kunden senden."
              />
            ) : (
              <div className="space-y-3">
                {offers.map((o) => (
                  <div
                    key={o.id}
                    className={`rounded-xl border p-4 ${
                      o.isChosen
                        ? "border-primary-container bg-primary-fixed/40 dark:border-primary-container dark:bg-primary-fixed/10"
                        : "border-outline-variant/40 bg-surface-container-low dark:border-outline/30 dark:bg-surface-variant/10"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                        Option {o.label}
                        {o.menuSelection?.title ? ` · ${o.menuSelection.title}` : ""}
                      </p>
                      {o.isChosen ? (
                        <span className="flex items-center gap-1 font-label-md text-label-md font-bold text-primary">
                          <Icon name="check_circle" className="text-[18px]" /> Gewählt
                        </span>
                      ) : event.status === "offer_sent" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAEFD9] px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-[#96650D]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#C98A1B]" /> Wartet auf Auswahl
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      {o.guestCount} Gäste · v{o.version}
                      {o.guestCount > 0 ? ` · ${money(Math.round(o.amountTotalCents / o.guestCount))} pro Gast` : ""}
                    </p>
                    <p className="mt-2 font-headline-sm text-headline-sm font-semibold text-on-background dark:text-inverse-on-surface">
                      {money(o.amountTotalCents)}
                    </p>
                    {o.isChosen && o.agbAccepted && (
                      <p className="mt-2 flex items-center gap-1.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        <Icon name="verified" className="text-[16px]" />
                        Verbindlich angenommen {formatDateTime(o.agbAcceptedAt)}
                        {o.chosenIp ? ` · IP ${o.chosenIp}` : ""}
                      </p>
                    )}
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
                      disabled={printBusy}
                      onClick={() => void printOfferDocument(o.id)}
                    >
                      <Icon name="picture_as_pdf" className="text-[16px]" /> Dokument (PDF)
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  {event.publicToken && event.offerSentAt ? (
                    <a
                      href={`/angebot/${event.publicToken}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 font-label-md text-label-md text-primary hover:underline"
                    >
                      <Icon name="open_in_new" className="text-[18px]" /> Öffentliche Angebotsseite öffnen
                    </a>
                  ) : (
                    <span />
                  )}
                  <Link
                    to={`/veranstaltungen/${event.id}/angebot`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-outline-variant px-3 py-1.5 font-label-md text-label-md text-secondary hover:bg-surface-container-high dark:border-outline dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/20"
                  >
                    <Icon name="add" className="text-[16px]" /> Weitere Option anbieten
                  </Link>
                </div>
              </div>
            )}
          </section>

          {/* L3: Abrechnung (Kostenübernahme + Rechnungen + Zahlungen gebündelt).
              id="abrechnung": Sprungziel des „Zur Abrechnung"-CTAs im Nächster-Schritt-Banner. */}
          <section id="abrechnung" className="card scroll-mt-20 p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className={CARD_TITLE}>Abrechnung</h3>
                <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:bg-surface-variant/30 dark:text-secondary-fixed-dim">
                  B2B
                </span>
              </div>
              {/* CX-Audit 2026-07-17: Querlink zur mandantenweiten Zahlungsübersicht. */}
              <span className="flex items-center gap-3 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                Kostenübernahme → Rechnung → Zahlungseingang
                <Link to="/zahlungen" className="font-label-md text-label-md text-primary hover:underline">
                  Alle Zahlungen
                </Link>
              </span>
            </div>

            <div className="divide-y divide-outline-variant/30 dark:divide-outline/20">
              {/* Zeile 1: Kostenübernahme */}
              <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
                <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                  <Icon name="draw" className="text-[18px]" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Kostenübernahme</p>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-label-md text-[11px] ${caQ.data ? caMeta(caQ.data.status).cls : NOT_REQUESTED.cls}`}>
                      {caQ.data ? caMeta(caQ.data.status).label : NOT_REQUESTED.label}
                    </span>
                  </div>
                  <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Verbindliche eSign-Zusage für Firmenkunden — Bedingungen aus den Einstellungen.
                  </p>
                  {caQ.data?.signerName && (
                    <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      {caQ.data.signerName}
                      {caQ.data.amountGrossCents != null ? ` · ${money(caQ.data.amountGrossCents)}` : ""}
                    </p>
                  )}
                  {caQ.data?.status === "signed" && caQ.data.signedPdfUrl ? (
                    <a href={caQ.data.signedPdfUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline">
                      <Icon name="picture_as_pdf" className="text-[16px]" /> Signierte Kostenübernahme (PDF)
                    </a>
                  ) : caQ.data?.signUrl ? (
                    <a href={caQ.data.signUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline">
                      <Icon name="open_in_new" className="text-[16px]" /> Signatur-Link öffnen
                    </a>
                  ) : null}
                </div>
                {!caQ.data && (
                  <div className="col-span-2 flex flex-wrap items-center gap-3 sm:col-span-1 sm:flex-col sm:items-end sm:gap-2">
                    <label className="flex items-center gap-1.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      <input type="checkbox" checked={caTest} onChange={(e) => setCaTest(e.target.checked)} /> Testvertrag
                    </label>
                    <button type="button" className="btn-secondary !py-1.5 !text-[11.5px]" disabled={caBusy} onClick={() => void requestCostAcceptance()}>
                      {caBusy ? "Wird erstellt …" : "Anfordern"}
                    </button>
                  </div>
                )}
              </div>

              {/* Zeile 2: Rechnungen */}
              <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
                <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                  <Icon name="receipt_long" className="text-[18px]" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Rechnung</p>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-label-md text-[11px] ${invoices.length > 0 ? invMeta(invoices[0]!.status).cls : NOT_REQUESTED.cls}`}>
                      {invoices.length > 0 ? invMeta(invoices[0]!.status).label : "Keine Rechnung"}
                    </span>
                    <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">LexOffice</span>
                  </div>
                  <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Aus der gewählten Angebotsvariante, als PDF versendbar.
                  </p>
                  {invoices.length > 0 && (
                    <ul className="mt-2 space-y-2">
                      {invoices.map((inv) => (
                        <li key={inv.id} className="rounded-lg border border-outline-variant/30 p-2.5 dark:border-outline/30">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                              {INV_TYPE_LABEL[inv.invoiceType]}
                            </span>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${invMeta(inv.status).cls}`}>
                              {invMeta(inv.status).label}
                            </span>
                            <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                              {inv.invoiceNumber ?? "—"}
                              {inv.amountGrossCents != null ? ` · ${money(inv.amountGrossCents)}` : ""}
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-3">
                            {inv.pdfUrl && (
                              <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline">
                                <Icon name="picture_as_pdf" className="text-[15px]" /> PDF
                              </a>
                            )}
                            {/* N-4: stornierte/laufende Storno-Rechnungen nicht erneut versendbar. */}
                            {inv.status !== "voided" && inv.status !== "voiding" && (
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline disabled:opacity-50" disabled={invBusy} onClick={() => void sendInvoice(inv.id)}>
                                <Icon name="send" className="text-[15px]" /> {inv.sentAt ? "Erneut senden" : "An Kunden senden"}
                              </button>
                            )}
                            {inv.status !== "voided" && inv.invoiceType !== "credit" && (
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-error hover:underline disabled:opacity-50" disabled={invBusy || inv.status === "voiding"} onClick={() => void cancelInvoice(inv.id)}>
                                <Icon name="undo" className="text-[15px]" /> Stornieren
                              </button>
                            )}
                            {inv.sentAt && (
                              <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">gesendet {formatDate(inv.sentAt)}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col sm:items-end">
                  {!hasFinalOrFull && (
                    <button type="button" className="btn-secondary !py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("deposit")}>
                      Anzahlung
                    </button>
                  )}
                  {hasDeposit && !hasFinalOrFull && (
                    <button type="button" className="btn-primary !py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("final")}>
                      Schlussrechnung
                    </button>
                  )}
                  {!hasDeposit && !hasFinalOrFull && (
                    <button type="button" className="btn-primary !py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("invoice")}>
                      {invBusy ? "Wird erstellt …" : "Vollrechnung"}
                    </button>
                  )}
                </div>
              </div>

              {/* Zeile 3: Zahlungen */}
              <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
                <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                  <Icon name="credit_card" className="text-[18px]" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Zahlungen</p>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FAEFD9] px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-[#96650D]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#C98A1B]" />
                      {paidCents > 0 && paidCents >= (event.amountTotalCents ?? 0) && (event.amountTotalCents ?? 0) > 0
                        ? "Vollständig bezahlt"
                        : `Offen · ${money(openCents)}`}
                    </span>
                  </div>
                  <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    {payments.length === 0
                      ? "Noch keine Zahlungen erfasst."
                      : `${money(paidCents)}${event.amountTotalCents ? ` von ${money(event.amountTotalCents)}` : ""} eingegangen.`}
                  </p>
                  {paymentsQ.loading ? (
                    <LoadingRows rows={1} />
                  ) : payments.length > 0 ? (
                    <ul className="mt-2 space-y-2">
                      {payments.map((p) => (
                        <li key={p.id} className="rounded-lg border border-outline-variant/30 p-2.5 dark:border-outline/30">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                                {PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}
                              </p>
                              <p className="mt-0.5 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
                                {p.dueDate ? `Fällig ${formatDate(p.dueDate)}` : formatDate(p.createdAt)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <p className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">{money(p.amountCents)}</p>
                              <StatusPill meta={PAYMENT_STATUS[p.status]} />
                            </div>
                          </div>
                          {p.status !== "succeeded" && p.status !== "refunded" && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-outline-variant/30 pt-2 dark:border-outline/20">
                              {p.checkoutUrl ? (
                                <>
                                  <a href={p.checkoutUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline">
                                    <Icon name="link" className="text-[15px]" /> Link
                                  </a>
                                  <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "send-link")}>
                                    <Icon name="send" className="text-[15px]" /> Senden
                                  </button>
                                </>
                              ) : (
                                <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "checkout-link")}>
                                  <Icon name="link" className="text-[15px]" /> Zahlungslink
                                </button>
                              )}
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-tertiary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "paid")}>
                                <Icon name="check_circle" className="text-[15px]" /> Bezahlt
                              </button>
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-error hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "delete")}>
                                <Icon name="delete" className="text-[15px]" /> Löschen
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col sm:items-end">
                  {payments.length === 0 && (
                    <button type="button" className="btn-secondary !py-1.5 !text-[11.5px]" disabled={busy} onClick={() => void createPaymentPlan()}>
                      Zahlungsplan
                    </button>
                  )}
                  <button type="button" className="btn-primary !py-1.5 !text-[11.5px]" onClick={() => setPayOpen(true)}>
                    <Icon name="add" className="text-[15px]" /> Zahlung
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* L4: Aktivität (E-Mail-Verlauf + Verlauf gebündelt, filterbar) */}
          <section className="card p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className={CARD_TITLE}>Aktivität</h3>
                <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">E-Mails &amp; Statuswechsel in einem Verlauf</p>
              </div>
              <div className="flex gap-1.5">
                {(["all", "emails", "status"] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setActivityFilter(f)}
                    className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${
                      activityFilter === f
                        ? "bg-primary-container text-primary"
                        : "border border-outline-variant/40 text-secondary hover:bg-surface-container-high dark:border-outline/30 dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/20"
                    }`}
                  >
                    {f === "all" ? "Alle" : f === "emails" ? "E-Mails" : "Status"}
                  </button>
                ))}
              </div>
            </div>
            {emailsQ.loading || activityQ.loading ? (
              <LoadingRows rows={3} />
            ) : groupedFeed.length === 0 ? (
              <EmptyState
                icon="history"
                title="Noch keine Einträge"
                hint="Statuswechsel und E-Mails zu diesem Vorgang erscheinen hier automatisch."
              />
            ) : (
              <ul className="space-y-3">
                {groupedFeed.map((group) => {
                  const expanded = expandedGroups.has(group.key);
                  return (
                    <li key={group.key} className="space-y-2">
                      <ul className="space-y-2">
                        {group.primary.kind === "email" ? (
                          <EmailEntryItem entry={group.primary} />
                        ) : (
                          (() => {
                            const meta = ACTIVITY_LABEL[group.primary.activity.action] ?? { label: group.primary.activity.action, icon: "history" };
                            return (
                              <li className="flex gap-3 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                                <Icon name={meta.icon} className="mt-0.5 shrink-0 text-[18px] text-secondary dark:text-secondary-fixed-dim" />
                                <div className="min-w-0">
                                  <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{meta.label}</p>
                                  <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                                    {formatDateTime(group.primary.activity.createdAt)} · {ACTOR_LABEL[group.primary.activity.actorType]}
                                    {group.primary.activity.actorLabel ? ` (${group.primary.activity.actorLabel})` : ""}
                                  </p>
                                </div>
                              </li>
                            );
                          })()
                        )}
                        {/* G6: weitere Zustellungen derselben Mail gebündelt, auf Wunsch einzeln anzeigen */}
                        {group.extra.length > 0 && !expanded && (
                          <li>
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.key)}
                              className="ml-1 font-label-md text-[11.5px] font-semibold text-primary hover:underline"
                            >
                              {group.extra.length} weitere Zustellung{group.extra.length === 1 ? "" : "en"} · Einzeln anzeigen
                            </button>
                          </li>
                        )}
                        {group.extra.length > 0 &&
                          expanded &&
                          group.extra.map((extra) =>
                            extra.kind === "email" ? (
                              <EmailEntryItem key={extra.id} entry={extra} />
                            ) : null,
                          )}
                        {group.extra.length > 0 && expanded && (
                          <li>
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.key)}
                              className="ml-1 font-label-md text-[11.5px] font-semibold text-secondary hover:underline dark:text-secondary-fixed-dim"
                            >
                              Einblenden schließen
                            </button>
                          </li>
                        )}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Seitenspalte */}
        <div className="flex flex-col gap-4">
          {/* R1: Eckdaten */}
          <div className="card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className={SIDE_TITLE}>Eckdaten</h3>
              <button type="button" className="font-label-md text-[12px] text-primary hover:underline" onClick={() => setEditOpen(true)}>
                Bearbeiten
              </button>
            </div>
            <dl className="space-y-3">
              {[
                ...(event.serviceType ? [{ dt: "Art", dd: event.serviceType as React.ReactNode }] : []),
                {
                  dt: "Datum",
                  dd: (
                    <span className="flex items-center gap-1.5">
                      {formatDate(event.eventDate)}
                      {isPast && (
                        <span className="rounded-full bg-[#FAEFD9] px-1.5 py-0.5 text-[10px] font-semibold text-[#96650D]">Vergangenheit</span>
                      )}
                    </span>
                  ),
                },
                {
                  dt: "Uhrzeit",
                  dd: event.timeFrom ? (
                    `${event.timeFrom.slice(0, 5)} – ${event.timeTo?.slice(0, 5) ?? "?"}`
                  ) : (
                    <button type="button" className="text-primary hover:underline" onClick={() => setEditOpen(true)}>
                      Ergänzen
                    </button>
                  ),
                },
                { dt: "Gäste", dd: event.guestCount ?? "–" },
                {
                  dt: "Ort",
                  dd: event.location ?? (
                    <button type="button" className="text-primary hover:underline" onClick={() => setEditOpen(true)}>
                      Ergänzen
                    </button>
                  ),
                },
                { dt: "Angebotswert", dd: money(event.amountTotalCents) },
                { dt: "Angebotsversion", dd: `v${event.currentOfferVersion}` },
                ...(["offer_chosen", "paid", "completed"].includes(event.status)
                  ? [
                      {
                        dt: "Menü",
                        dd: event.menuConfirmedAt ? (
                          <span className="flex items-center gap-1.5 text-primary dark:text-primary-fixed-dim">
                            <Icon name="check_circle" className="text-[15px]" />
                            {`bestätigt am ${formatDate(event.menuConfirmedAt)}`}
                          </span>
                        ) : (
                          <span className="text-[#96650D]">Noch nicht bestätigt</span>
                        ),
                      },
                    ]
                  : []),
              ].map((row) => (
                <div key={row.dt} className="flex items-center justify-between gap-3">
                  <dt className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {row.dt}
                  </dt>
                  <dd className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    {row.dd}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* R1b: Versionsarchiv (Block 3) — read-only Liste je Versand-Snapshot */}
          {historyQ.data && historyQ.data.length > 0 && (
            <div className="card p-6">
              <h3 className={`mb-4 ${SIDE_TITLE}`}>Versionsarchiv</h3>
              <ul className="space-y-3">
                {historyQ.data.map((h) => {
                  const total = h.optionsSnapshot.reduce((sum, o) => sum + (o.amountTotalCents ?? 0), 0);
                  const hasItems = h.optionsSnapshot.every((o) => Array.isArray(o.items) && Array.isArray(o.days));
                  return (
                    <li key={h.id} className="flex items-center justify-between gap-3 border-b border-outline-variant/40 pb-3 last:border-0 last:pb-0">
                      <div className="min-w-0">
                        <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                          v{h.version} · {h.sentAt ? formatDate(h.sentAt) : "–"}
                        </p>
                        <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                          {h.optionsSnapshot.length} {h.optionsSnapshot.length === 1 ? "Option" : "Optionen"} · {money(total)}
                        </p>
                      </div>
                      {hasItems ? (
                        <button
                          type="button"
                          className="shrink-0 rounded-lg border border-outline-variant px-2.5 py-1.5 font-label-md text-[11.5px] font-semibold text-on-surface-variant hover:bg-surface-container disabled:opacity-50"
                          disabled={historyBusy === h.version}
                          onClick={() => restoreVersion(h.version)}
                        >
                          {historyBusy === h.version ? "…" : "Als Entwurf kopieren"}
                        </button>
                      ) : (
                        <span className="shrink-0 font-body-sm text-[11px] text-on-surface-variant/60" title="Version vor Archiv-Upgrade — nur Zusammenfassung verfügbar">
                          Nur Zusammenfassung
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* R2: Kunde */}
          <div className="card p-6">
            <h3 className={`mb-4 ${SIDE_TITLE}`}>Kunde</h3>
            {customer ? (
              <div>
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                  {customer.name}
                </p>
                {customer.company && (
                  <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {customer.company}
                  </p>
                )}
                <p className="mt-2 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                  <Icon name="mail" className="text-[18px]" /> {customer.email}
                </p>
                {customer.phone && (
                  <p className="mt-1 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="call" className="text-[18px]" /> {customer.phone}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to={`/kunden/${customer.id}`}
                    className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline"
                  >
                    Kundenprofil öffnen
                    <Icon name="arrow_forward" className="text-[18px]" />
                  </Link>
                  <a href={`mailto:${customer.email}`} className="btn-secondary !py-1.5 !text-[11.5px]">
                    <Icon name="mail" className="text-[15px]" /> E-Mail schreiben
                  </a>
                </div>
              </div>
            ) : (
              <LoadingRows rows={1} />
            )}
          </div>

          {/* R2b: Wetter (Datenanreicherung Welle A) — nur wenn tenant-weit aktiviert */}
          {wetterEnabled && (
            <div className="card p-6">
              <h3 className={`mb-4 ${SIDE_TITLE}`}>Wetter</h3>
              {!event.locationType ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                  Ort-Typ nicht gepflegt —{" "}
                  <button type="button" className="text-primary hover:underline" onClick={() => setEditOpen(true)}>
                    in „Bearbeiten" ergänzen
                  </button>
                  .
                </p>
              ) : !event.eventDate ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant/70">Kein Termin gesetzt.</p>
              ) : !weatherWindowOk ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                  Prognose erst ab ca. 10 Tage vor dem Termin verfügbar.
                </p>
              ) : weatherQ.loading ? (
                <LoadingRows rows={1} />
              ) : weatherQ.data ? (
                <div>
                  <p className="font-body-md text-body-md font-semibold capitalize text-on-background dark:text-inverse-on-surface">
                    {weatherQ.data.description ?? "—"}
                  </p>
                  <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {weatherQ.data.tempMinC != null && weatherQ.data.tempMaxC != null
                      ? `${Math.round(weatherQ.data.tempMinC)}° – ${Math.round(weatherQ.data.tempMaxC)}°`
                      : "–"}
                    {weatherQ.data.precipitationMm != null && weatherQ.data.precipitationMm > 0
                      ? ` · ${weatherQ.data.precipitationMm} mm Niederschlag`
                      : ""}
                  </p>
                  <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                    {event.locationType === "in_house" ? "Betriebsadresse" : "Kundenadresse"} · Bright Sky (DWD)
                  </p>
                </div>
              ) : (
                <p className="font-body-sm text-body-sm text-on-surface-variant/70">Keine Wetterdaten verfügbar.</p>
              )}
            </div>
          )}

          {/* Backlog #41: Anhänge (Chat-Referenzfotos, ueber die Anfrage geclaimt) - nur sichtbar,
              wenn tatsaechlich welche vorliegen. */}
          {Boolean((event.details as { attachments?: EventAttachment[] } | null)?.attachments?.length) && (
            <div className="card p-6">
              <h3 className={`mb-4 ${SIDE_TITLE}`}>Anhänge</h3>
              <div className="space-y-2">
                {((event.details as { attachments?: EventAttachment[] } | null)?.attachments ?? []).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg bg-surface-container-high px-3 py-2 text-left font-body-sm text-body-sm text-primary hover:underline dark:bg-surface-variant/30"
                    onClick={() => void openAuthenticatedFile(`/api/events/${id}/attachments/${a.id}`)}
                  >
                    <Icon name="image" className="text-[18px]" /> Anhang ansehen
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* R3: Aufgaben */}
          <div className="card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className={SIDE_TITLE}>Aufgaben</h3>
              <span className="flex items-center gap-2.5">
                {(tasksQ.data ?? []).length > 0 && (
                  <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[11px] text-secondary dark:bg-surface-variant/30 dark:text-secondary-fixed-dim">
                    {(tasksQ.data ?? []).filter((t) => t.status === "done").length} / {(tasksQ.data ?? []).length}
                  </span>
                )}
                {/* CX-Audit 2026-07-17: Querlink zum Aufgaben-Board (vorher nur lokal abhakbar). */}
                <Link to="/aufgaben" className="font-label-md text-label-md text-primary hover:underline">
                  Board
                </Link>
              </span>
            </div>
            {tasksQ.loading ? (
              <LoadingRows rows={2} />
            ) : (
              <div className="space-y-2">
                {(tasksQ.data ?? []).length === 0 && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant/70">Noch keine Aufgaben.</p>
                )}
                {(tasksQ.data ?? []).map((t) => {
                  const overdue = t.status !== "done" && t.dueDate && new Date(t.dueDate) < new Date();
                  return (
                    <div key={t.id} className="flex items-start gap-2.5">
                      <button
                        type="button"
                        disabled={taskBusy}
                        onClick={() => void toggleTask(t)}
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] transition-colors ${
                          t.status === "done" ? "border-primary bg-primary text-on-primary" : "border-outline-variant dark:border-outline"
                        }`}
                        aria-label={t.status === "done" ? "Als offen markieren" : "Als erledigt markieren"}
                      >
                        {t.status === "done" && <Icon name="check" className="text-[12px]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={`font-body-sm text-body-sm ${t.status === "done" ? "text-secondary-fixed-dim line-through" : "text-on-background dark:text-inverse-on-surface"}`}>
                          {t.title}
                        </p>
                        {t.dueDate && t.status !== "done" && (
                          <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${overdue ? "bg-error-container text-error" : "bg-[#FAEFD9] text-[#96650D]"}`}>
                            {overdue ? "überfällig" : `bis ${formatDate(t.dueDate)}`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <form onSubmit={(e) => void addTask(e)} className="mt-3 flex gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
              <input
                className="input !py-1.5 text-body-sm"
                placeholder="+ Aufgabe hinzufügen …"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
              />
              <button type="submit" className="btn-ghost !px-2" disabled={taskBusy || !newTaskTitle.trim()}>
                <Icon name="add" className="text-[18px]" />
              </button>
            </form>
          </div>

          {cancellationTodos && (
            <div className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30">
              <p>Storniert – {cancellationTodos.canceledPayments ?? 0} offene Zahlung(en) entwertet.</p>
              {(cancellationTodos.openInvoices?.length ?? 0) > 0 && (
                <p className="mt-1">Noch zu tun: {cancellationTodos.openInvoices!.length} offene Rechnung(en) stornieren (oben „Stornieren").</p>
              )}
              {(cancellationTodos.paidPayments?.length ?? 0) > 0 && (
                <p className="mt-1">{cancellationTodos.paidPayments!.length} bezahlte Zahlung(en) ggf. erstatten.</p>
              )}
            </div>
          )}

          {/* R4: Vorgang stornieren (bare Danger-Link, kein Karten-Wrapper) */}
          {event.status !== "cancelled" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void cancelEvent()}
              className="inline-flex items-center gap-1.5 self-start px-1 font-label-md text-label-md text-error hover:underline disabled:opacity-50"
            >
              <Icon name="cancel" className="text-[16px]" /> Vorgang stornieren
            </button>
          )}
        </div>
      </div>

      {/* Bearbeiten: Kernfelder der Veranstaltung */}
      <Drawer open={editOpen} onClose={() => setEditOpen(false)} title="Veranstaltung bearbeiten">
        <form className="space-y-4" onSubmit={(e) => void saveEdit(e)}>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Anlass</span>
            <input name="occasion" className="input" defaultValue={event.occasion ?? ""} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Art</span>
            <input name="serviceType" className="input" placeholder="z. B. Hochzeit · Dinner & Feier" defaultValue={event.serviceType ?? ""} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Ort-Typ</span>
            <select name="locationType" className="input" defaultValue={event.locationType ?? ""}>
              <option value="">Nicht gepflegt</option>
              <option value="in_house">Im Haus (Event)</option>
              <option value="off_site">Außer Haus (Catering)</option>
            </select>
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Steuert, ob das Wetter-Widget die Betriebs- oder die Kundenadresse nutzt.
            </span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Datum</span>
              <input name="eventDate" type="date" className="input" defaultValue={event.eventDate ?? ""} />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Von</span>
              <input name="timeFrom" type="time" className="input" defaultValue={event.timeFrom?.slice(0, 5) ?? ""} />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Bis</span>
              <input name="timeTo" type="time" className="input" defaultValue={event.timeTo?.slice(0, 5) ?? ""} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Gäste</span>
              <input name="guestCount" type="number" min="0" className="input" defaultValue={event.guestCount ?? ""} />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Ort</span>
              <input name="location" className="input" defaultValue={event.location ?? ""} />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Stammdaten-Ort</span>
            {/* A-01: key an den Ladezustand koppeln -> sobald die Optionen da sind, mountet das
                Select neu und wendet defaultValue korrekt an (uncontrolled defaultValue greift
                sonst nicht, wenn die Optionen erst nach dem ersten Render eintreffen). */}
            <select key={locationsQ.data ? "loc-ready" : "loc-loading"} name="locationId" className="input" defaultValue={event.locationId ?? ""}>
              <option value="">Kein Ort ausgewählt</option>
              {(locationsQ.data ?? []).map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                  {loc.isActive ? "" : " (inaktiv)"}
                </option>
              ))}
            </select>
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Räume/Kapazitäten verwalten unter Einstellungen → Orte.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Angebotssprache</span>
            <select name="offerLanguage" className="input" defaultValue={event.offerLanguage ?? "de"}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Steuert die öffentliche Angebotsseite + die Angebots-Mail.
            </span>
          </label>
          <div className="flex justify-end gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <button type="button" className="btn-secondary" onClick={() => setEditOpen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={editBusy}>
              {editBusy ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </form>
      </Drawer>

      {/* Zahlung anlegen */}
      <Drawer open={payOpen} onClose={() => setPayOpen(false)} title="Zahlung anlegen">
        <form className="space-y-4" onSubmit={(e) => void createPayment(e)}>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Betrag (€) *
            </span>
            <input name="amount" required className="input" inputMode="decimal" placeholder="1.500,00" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Typ
            </span>
            <select name="paymentType" className="input" defaultValue="deposit">
              {Object.entries(PAYMENT_TYPE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Fällig am
            </span>
            <input name="dueDate" type="date" className="input" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setPayOpen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Speichert …" : "Zahlung anlegen"}
            </button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
